import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import scrollToTop from "../../../utils/scrollToTop.js";
import useCreateUrl from "../../../hooks/useCreateUrl.js";
import { SEARCH_PAGE_SIZE } from "@flights/constants";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { actions } from "../../../store/search/actions.js";
import { useLocation, useHistory } from "react-router-dom";
import { useGetAvailableFilters } from "../SearchResults/components/boundsFilterHooks.js";
import { getSearchResultsVisibleIndexes } from "../../../store/searchResults/selectors.js";
export default function useSearchPaginationController() {
    const i18n = useI18n();
    const dispatch = useDispatch();
    const history = useHistory();
    const { search, pathname } = useLocation();
    const { enhanceURLSearchParams } = useCreateUrl();
    const { totalCount, filteredTotalCount } = useGetAvailableFilters();
    const qs = useMemo(() => new URLSearchParams(search), [search]);
    const totalPages = useMemo(() => {
        const total = totalCount ? Math.ceil(totalCount / SEARCH_PAGE_SIZE) : 0;
        return filteredTotalCount ? Math.ceil(filteredTotalCount / SEARCH_PAGE_SIZE) : total;
    }, [totalCount, filteredTotalCount]);
    const currentPage = useMemo(() => {
        return Number(qs.get("page") || 1);
    }, [qs]);
    const goToPage = useCallback((page, disableAutoscroll = false, isAutoLoad = false) => {
        const target = Math.max(1, Math.min(page, totalPages));
        dispatch(actions.setPage(target));
        if (!disableAutoscroll)
            scrollToTop();
        qs.set("page", `${target}`);
        if (isAutoLoad)
            qs.set("auto_load_page", "true");
        enhanceURLSearchParams(qs);
        history.push({ pathname: pathname, search: qs.toString() }, { hideLoader: true });
    }, [qs, history, dispatch, pathname, enhanceURLSearchParams, totalPages]);
    const onPageChange = useCallback(({ page }) => {
        goToPage(page);
    }, [goToPage]);
    const searchResultsVisibleIndexes = useSelector(getSearchResultsVisibleIndexes);
    const canRender = useMemo(() => !searchResultsVisibleIndexes && totalPages > 1, [searchResultsVisibleIndexes, totalPages]);
    return {
        canRender,
        totalPages,
        currentPage,
        onPageChange,
        goToPage,
        ariaPageLabelPrefix: "",
        ariaPreviousLabel: i18n.trans(t("flights_action_back")),
        ariaNextLabel: i18n.trans(t("flights_action_next"))
    };
}
